<template>
    <v-card variant="flat">
        <v-card-text>
            <div class="d-flex">
                <div class="flex-1-0">
                    <h1><v-icon icon="mdi-account-check-outline" class="mr-1"></v-icon> Attempts</h1>
                </div>
                <div class="d-flex">
                    <v-btn color="primary"
                           @click="showAdd = true"
                           prepend-icon="mdi-plus">
                        CREATE LINK
                    </v-btn>
                </div>
            </div>
            <v-dialog v-model="showAdd"
                      persistent
                      width="500"
                      :scrim="false">
                <v-card>
                    <v-toolbar dark
                               color="primary">
                        <v-toolbar-title><v-icon icon="mdi-plus"></v-icon> Creating a test link</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon
                               dark
                               @click="showAdd = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <v-card-text>
                        <v-text-field v-model="newAttemptName"
                                      label="Employee Name *"
                                      density="compact"
                                      hide-details
                                      @keyup.enter="createAttempt"></v-text-field>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary"
                               :loading="creatingAttempt"
                               :disabled="!newAttemptName.trim()"
                               prepend-icon="mdi-link"
                               @click="createAttempt">
                            Generate Link
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Attempts Table -->
            <v-skeleton-loader v-if="loading"
                               class="mx-auto"
                               type="table"></v-skeleton-loader>
            <v-data-table v-else
                          :headers="headers"
                          :items="attempts"
                          density="comfortable"
                          hover>
                <template v-slot:item.status="{ item }">
                    <v-chip :color="statusColor(item.status)" size="small" variant="flat">
                        {{ statusLabel(item.status) }}
                    </v-chip>
                </template>
                <template v-slot:item.testLink="{ item }">
                    <v-text-field :model-value="attemptLink(item.token)"
                                  readonly
                                  density="compact"
                                  hide-details
                                  variant="outlined"
                                  bg-color="white">
                        <template v-slot:append-inner>
                            <v-btn icon="mdi-content-copy"
                                   size="small"
                                   variant="text"
                                   @click="copyLink(item.token)"></v-btn>
                        </template>
                    </v-text-field>
                </template>
                <template v-slot:item.timeTakenSeconds="{ item }">
                    {{ item.timeTakenSeconds != null ? formatTime(item.timeTakenSeconds) : '—' }}
                </template>
                <template v-slot:item.score="{ item }">
                    <span v-if="item.status === 2" :class="scoreColor(item.correctAnswers, item.totalAnswered)">
                        {{ item.correctAnswers }} / {{ item.totalAnswered }}
                    </span>
                    <span v-else class="text-medium-emphasis">—</span>
                </template>
                <template v-slot:item.actions="{ item }">
                    <v-btn v-if="item.status === 2"
                           icon="mdi-eye"
                           size="small"
                           variant="text"
                           color="primary"
                           title="View Results"
                           @click="viewResults(item)"></v-btn>
                    <v-btn v-if="item.status !== 2"
                           icon="mdi-content-copy"
                           size="small"
                           variant="text"
                           title="Copy Link"
                           @click="copyLink(item.token)"></v-btn>
                </template>
            </v-data-table>

            <!-- Results Dialog -->
            <v-dialog v-model="showResults" max-width="750" scrollable>
                <v-card v-if="selectedAttempt">
                    <v-toolbar color="primary" dark density="compact">
                        <v-toolbar-title>Results: {{ selectedAttempt.participantName }}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon="mdi-close" @click="showResults = false"></v-btn>
                    </v-toolbar>
                    <v-card-subtitle class="pt-3 pb-1">
                        <v-chip color="primary" size="small" class="mr-2">
                            Score: {{ selectedAttempt.correctAnswers }} / {{ selectedAttempt.totalAnswered }}
                        </v-chip>
                        <v-chip color="secondary" size="small" v-if="selectedAttempt.timeTakenSeconds != null">
                            Time: {{ formatTime(selectedAttempt.timeTakenSeconds) }}
                        </v-chip>
                    </v-card-subtitle>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                        <div v-if="loadingResults" class="text-center pa-6">
                            <v-progress-circular indeterminate></v-progress-circular>
                        </div>
                        <v-list v-else-if="selectedAttemptDetail" lines="two">
                            <v-list-item v-for="(answer, i) in selectedAttemptDetail.answers"
                                         :key="answer.quizQuestionId"
                                         :style="answer.isCorrect
                        ? 'border-left: 4px solid #4caf50; background: rgba(76,175,80,0.04)'
                        : 'border-left: 4px solid #f44336; background: rgba(244,67,54,0.04)'"
                                         class="mb-1">
                                <template v-slot:prepend>
                                    <v-icon :color="answer.isCorrect ? 'success' : 'error'" size="small">
                                        {{ answer.isCorrect ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                    </v-icon>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium">
                                    Q{{ i + 1 }}. {{ answer.questionName }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    <span>Answered: </span>
                                    <strong :class="answer.isCorrect ? 'text-success' : 'text-error'">
                                        {{ answer.selectedOptionName || 'No answer' }}
                                    </strong>
                                    <span v-if="!answer.isCorrect">
                                        &nbsp;&bull; Correct: <strong class="text-success">{{ answer.correctOptionName }}</strong>
                                    </span>
                                    <span class="text-medium-emphasis ml-2">({{ answer.timeSpentSeconds }}s)</span>
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    name: 'QuizAttemptsPanel',
    props: {
        object: Object,
        entity: Object
    },
    data() {
        return {
            loading: false,
            attempts: [],
            showAdd: false,
            newAttemptName: '',
            creatingAttempt: false,
            lastCreatedAttempt: null,
            showResults: false,
            loadingResults: false,
            selectedAttempt: null,
            selectedAttemptDetail: null,
            headers: [
                { title: 'Employee', key: 'participantName', width: '20%' },
                { title: 'Status', key: 'status', width: '10%' },
                { title: 'Time Taken', key: 'timeTakenSeconds', width: '10%' },
                { title: 'Link', key: 'testLink', width: '30%' },
                { title: 'Score', key: 'score', width: '10%' },
                { title: '', key: 'actions', sortable: false, align: 'end', width: '10%' }
            ]
        };
    },
    mounted() {
        this.loadAttempts();
    },
    watch: {
        'object.id': function () { this.loadAttempts(); }
    },
    methods: {
        ...mapActions(['showSuccessSnack', 'showErrorSnack']),
        loadAttempts() {
            if (!this.quizId) return;
            this.loading = true;
            this.$api.get(`quiz/${this.quizId}/attempts`)
                .then(res => { this.attempts = res.data; })
                .catch(() => this.showErrorSnack('Failed to load attempts'))
                .finally(() => { this.loading = false; });
        },
        createAttempt() {
            if (!this.newAttemptName.trim()) return;
            this.creatingAttempt = true;
            this.$api.post(`quiz/${this.quizId}/attempts`, { participantName: this.newAttemptName })
                .then(res => {
                    this.newAttemptName = '';
                    this.loadAttempts();
                    this.showSuccessSnack('Link generated!');
                    this.showAdd = false;
                })
                .catch(() => this.showErrorSnack('Failed to create attempt'))
                .finally(() => { this.creatingAttempt = false; });
        },
        attemptLink(token) {
            return `${window.location.origin}/#/quiz/take/${token}`;
        },
        copyLink(token) {
            navigator.clipboard.writeText(this.attemptLink(token));
            this.showSuccessSnack('Link copied to clipboard!');
        },
        viewResults(attempt) {
            this.selectedAttempt = attempt;
            this.selectedAttemptDetail = null;
            this.showResults = true;
            this.loadingResults = true;
            this.$api.get(`quiz/take/${attempt.token}`)
                .then(res => { this.selectedAttemptDetail = res.data; })
                .catch(() => this.showErrorSnack('Failed to load results'))
                .finally(() => { this.loadingResults = false; });
        },
        statusLabel(status) {
            return ['Pending', 'In Progress', 'Completed', 'Expired'][status] ?? 'Unknown';
        },
        statusColor(status) {
            return ['grey', 'blue', 'success', 'error'][status] ?? 'grey';
        },
        scoreColor(correct, total) {
            if (!total) return '';
            const pct = correct / total;
            if (pct >= 0.8) return 'text-success';
            if (pct >= 0.5) return 'text-warning';
            return 'text-error';
        },
        formatTime(seconds) {
            const m = Math.floor(seconds / 60);
            const s = seconds % 60;
            return m > 0 ? `${m}m ${s}s` : `${s}s`;
        }
    },
    computed: {
        ...mapState({
            entityRecord: state => state.entities.entityRecord
        }),
        quizId() {
            return this.object?.id ?? this.entityRecord?.id;
        }
    }
};
</script>
